import type { ReactNode } from 'react';
import { Sparkline } from './charts/Sparkline';
import { fmtCallSample, fmtReq, fmtSec, pctFine } from '../lib/format';
import { USAGE_DISPLAY_MIN } from '../lib/networkUsage';
import { m } from '../paraglide/messages';

/** One measured figure on its own surface: quiet label, loud value, the sample
 *  it was measured on. The card is what stops the number floating. */
const Card = ({
  label,
  value,
  sub,
  lead,
  chart,
}: {
  label: string;
  value: string;
  sub?: ReactNode;
  /** a status dot or other mark set before the value */
  lead?: ReactNode;
  chart?: ReactNode;
}) => (
  <div className="min-w-0 rounded-12 border border-rule bg-wash px-3 py-2.5 sm:px-4 sm:py-3">
    <div className="truncate text-12 text-text-3">{label}</div>
    {/* A fixed value-row height so the card carrying a spline stays exactly as
        tall as one that does not, and every value in the row lines up. */}
    <div className="mt-1.5 flex min-h-6.5 items-end gap-2.5">
      <span className="mono flex items-center gap-1.5 text-18 font-medium leading-none tracking-[-0.02em] text-text s480:text-20 s980:text-22">
        {lead}
        {value}
      </span>
      {chart}
    </div>
    <div className="mono mt-1.5 h-4 truncate text-11 text-text-3">{sub}</div>
  </div>
);

export interface MeasuredCardsProps {
  /** window uptime, already gated by hasBenchmarkPopulation; omit to hide */
  uptimePct?: number;
  /** exact uptime population, when the gateway reports one */
  uptimeSample?: number;
  /** median response, already gated the same way; omit to hide */
  latencyP50Ms?: number;
  /** how the gateway labels its health window, e.g. `30d` */
  window?: string;
  /** requests over the trailing 30-day window */
  requests: number;
  /** weekly request series, oldest first; omit when it cannot be charted */
  weekly?: number[];
  className?: string;
}

/**
 * The three measured figures, each on its own bordered card, in a row inside
 * the page header: uptime with the calls behind it, requests served with their
 * weekly spline, and median response. Each is gated on having a real
 * population, and the card is what stops the number floating on bare page
 * background.
 */
export const MeasuredCards = ({
  uptimePct,
  uptimeSample,
  latencyP50Ms,
  window = '30d',
  requests,
  weekly,
  className = '',
}: MeasuredCardsProps) => {
  const hasRequests = requests >= USAGE_DISPLAY_MIN;
  const buckets = weekly?.length ?? 0;
  return (
    <div className={`grid grid-cols-3 items-stretch gap-2 sm:gap-3 ${className}`}>
      {uptimePct !== undefined && (
        <Card
          label={m.measure_uptime()}
          value={pctFine(uptimePct)}
          lead={<span className="size-1.5 rounded-full bg-pos" />}
          sub={
            uptimeSample !== undefined
              ? `${window} · ${fmtCallSample(uptimeSample)}`
              : m.measure_uptime_measured({ window })
          }
        />
      )}
      <Card
        label={m.measure_requests()}
        value={hasRequests ? fmtReq(requests) : '-'}
        chart={
          weekly && buckets > 1 ? (
            <Sparkline
              values={weekly}
              width={120}
              height={26}
              pad={3}
              dot
              color="var(--accent)"
              className="hidden h-6.5 w-30 shrink-0 s720:block"
              aria-label={m.measure_requests_chart_label({ count: buckets })}
            />
          ) : undefined
        }
        sub={weekly && buckets > 1 ? m.measure_requests_weekly({ count: buckets }) : '30d'}
      />
      {latencyP50Ms !== undefined && (
        <Card
          label={m.measure_response()}
          value={fmtSec(latencyP50Ms)}
          sub={m.measure_response_median({ window })}
        />
      )}
    </div>
  );
};
